import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
});

api.interceptors.response.use(
  res => res,
  async err => {
    if (err.response?.status === 401 && !err.config._retry) {
      err.config._retry = true;
      try {
        await api.post('/auth/refresh');
        return api(err.config);
      } catch {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// Convenience helpers
export const auth = {
  me: () => api.get('/auth/me'),
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  signupWithProject: (data) => api.post('/auth/signup-with-project', data),
  logout: () => api.post('/auth/logout'),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  magicLinkRequest: (email) => api.post('/auth/magic-link/request', { email }),
  magicLinkVerify: (token) => api.post('/auth/magic-link/verify', { token }),
};

export const publicApi = {
  freeAudit: (data) => api.post('/public/free-audit', data),
};

export const clients = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  summary: (id) => api.get(`/clients/${id}/summary`),
  resetPassword: (id) => api.post(`/clients/${id}/reset-password`),
  portalUrl: (id) => api.get(`/clients/${id}/portal-url`),
};

export const brands = {
  list: () => api.get('/brands'),
  get: (id) => api.get(`/brands/${id}`),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
};

export const projects = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateStatus: (id, status) => api.patch(`/projects/${id}/status`, { status }),
};

export const vault = {
  list: (projectId) => api.get(`/projects/${projectId}/vault`),
  create: (projectId, data) => api.post(`/projects/${projectId}/vault`, data),
  reveal: (projectId, entryId) => api.post(`/projects/${projectId}/vault/${entryId}/reveal`),
  update: (projectId, entryId, data) => api.put(`/projects/${projectId}/vault/${entryId}`, data),
  delete: (projectId, entryId) => api.delete(`/projects/${projectId}/vault/${entryId}`),
};

export const documents = {
  list: (projectId) => api.get(`/projects/${projectId}/documents`),
  upload: (projectId, formData) => api.post(`/projects/${projectId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (projectId, docId) => api.delete(`/projects/${projectId}/documents/${docId}`),
};

export const bugs = {
  list: (projectId) => api.get(`/projects/${projectId}/bugs`),
  create: (projectId, data) => api.post(`/projects/${projectId}/bugs`, data),
  updateStatus: (projectId, bugId, status) => api.patch(`/projects/${projectId}/bugs/${bugId}/status`, { status }),
  addComment: (projectId, bugId, content) => api.post(`/projects/${projectId}/bugs/${bugId}/comments`, { content }),
};

export const timeTracking = {
  list: (projectId) => api.get(`/projects/${projectId}/time`),
  create: (projectId, data) => api.post(`/projects/${projectId}/time`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/time/${id}`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/time/${id}`),
};

export const sows = {
  list: (projectId) => api.get(`/projects/${projectId}/sows`),
  create: (projectId, data) => api.post(`/projects/${projectId}/sows`, data),
  update: (projectId, id, data) => api.put(`/projects/${projectId}/sows/${id}`, data),
  share: (projectId, id) => api.post(`/projects/${projectId}/sows/${id}/share`),
  sign: (projectId, id, data) => api.post(`/projects/${projectId}/sows/${id}/sign`, data),
  delete: (projectId, id) => api.delete(`/projects/${projectId}/sows/${id}`),
  draft: (projectId, estimate_id) => api.post(`/projects/${projectId}/sows/draft-from-estimate`, { estimate_id }),
  pdfUrl: (projectId, id) => `${BASE}/projects/${projectId}/sows/${id}/pdf`,
};

export const webhooksApi = {
  list: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  test: (id) => api.post(`/webhooks/${id}/test`),
};

export const chat = {
  messages: (projectId) => api.get(`/projects/${projectId}/chat`),
  send: (projectId, content) => api.post(`/projects/${projectId}/chat`, { content }),
  unread: (projectId) => api.get(`/projects/${projectId}/chat/unread`),
};

export const estimates = {
  list: (projectId) => api.get(`/projects/${projectId}/estimates`),
  create: (projectId, data) => api.post(`/projects/${projectId}/estimates`, data),
  share: (projectId, estId) => api.post(`/projects/${projectId}/estimates/${estId}/share`),
  approve: (projectId, estId, note) => api.post(`/projects/${projectId}/estimates/${estId}/approve`, { note }),
  reject: (projectId, estId, note) => api.post(`/projects/${projectId}/estimates/${estId}/reject`, { note }),
  pdfUrl: (projectId, estId) => `${BASE}/projects/${projectId}/estimates/${estId}/pdf`,
};

export const invoices = {
  list: (projectId) => api.get(`/projects/${projectId}/invoices`),
  create: (projectId, data) => api.post(`/projects/${projectId}/invoices`, data),
  updateStatus: (projectId, invId, status) => api.patch(`/projects/${projectId}/invoices/${invId}/status`, { status }),
  recordPayment: (projectId, invId, data) => api.post(`/projects/${projectId}/invoices/${invId}/record-payment`, data),
  pdfUrl: (projectId, invId) => `${BASE}/projects/${projectId}/invoices/${invId}/pdf`,
  payStripe: (projectId, invId) => api.post(`/projects/${projectId}/invoices/${invId}/pay/stripe`),
  payPaypal: (projectId, invId) => api.post(`/projects/${projectId}/invoices/${invId}/pay/paypal`),
  payRazorpay: (projectId, invId) => api.post(`/projects/${projectId}/invoices/${invId}/pay/razorpay`),
  verifyStripe: (sessionId, invoiceId) => api.get(`/payments/stripe/verify`, { params: { session_id: sessionId, invoice_id: invoiceId } }),
  capturePaypal: (orderId, invoiceId) => api.post(`/payments/paypal/capture`, { order_id: orderId, invoice_id: invoiceId }),
  verifyRazorpay: (data) => api.post(`/payments/razorpay/verify`, data),
};

export const emails = {
  sendInvoice: (invoice_id, to_email) => api.post('/emails/send-invoice', { invoice_id, to_email }),
  sendEstimate: (estimate_id, to_email) => api.post('/emails/send-estimate', { estimate_id, to_email }),
  logs: () => api.get('/emails/logs'),
};

export const calendar = {
  status: () => api.get('/calendar/status'),
  oauthStart: () => api.get('/calendar/oauth/start'),
  disconnect: () => api.delete('/calendar/disconnect'),
  getRules: () => api.get('/calendar/availability-rules'),
  putRules: (data) => api.put('/calendar/availability-rules', data),
  bookings: () => api.get('/calendar/bookings'),
  publicSlots: (operatorId, date) => api.get(`/calendar/public/operators/${operatorId}/slots`, { params: { date } }),
  publicBook: (operatorId, data) => api.post(`/calendar/public/operators/${operatorId}/book`, data),
  overrides: () => api.get('/calendar/availability-overrides'),
  addOverride: (data) => api.post('/calendar/availability-overrides', data),
  deleteOverride: (id) => api.delete(`/calendar/availability-overrides/${id}`),
};

export const recurring = {
  list: () => api.get('/recurring-invoices'),
  listForProject: (projectId) => api.get(`/recurring-invoices/project/${projectId}`),
  create: (data) => api.post('/recurring-invoices', data),
  update: (id, data) => api.put(`/recurring-invoices/${id}`, data),
  delete: (id) => api.delete(`/recurring-invoices/${id}`),
  runNow: (id) => api.post(`/recurring-invoices/${id}/run-now`),
};

export const forms = {
  list: () => api.get('/forms'),
  create: (data) => api.post('/forms', data),
  get: (id) => api.get(`/forms/${id}`),
  update: (id, data) => api.put(`/forms/${id}`, data),
  delete: (id) => api.delete(`/forms/${id}`),
  submissions: (id) => api.get(`/forms/${id}/submissions`),
  public: (slug) => api.get(`/forms/public/${slug}`),
  submit: (slug, data) => api.post(`/forms/public/${slug}/submit`, { data }),
};

export const aiApi = {
  skills: () => api.get('/ai/skills'),
  createSkill: (data) => api.post('/ai/skills', data),
  updateSkill: (id, data) => api.put(`/ai/skills/${id}`, data),
  deleteSkill: (id) => api.delete(`/ai/skills/${id}`),
  runSkill: (data) => api.post('/ai/run', data),
  latestBrief: (clientId) => api.get(`/ai/brief/${clientId}/latest`),
  runs: (params) => api.get('/ai/runs', { params }),
};

export const catalog = {
  list: (params) => api.get('/catalog', { params }),
  categories: () => api.get('/catalog/categories'),
  create: (data) => api.post('/catalog', data),
  update: (id, data) => api.put(`/catalog/${id}`, data),
  delete: (id) => api.delete(`/catalog/${id}`),
  touch: (id) => api.post(`/catalog/${id}/touch`),
};

export const wizards = {
  list: () => api.get('/wizards'),
  create: (data) => api.post('/wizards', data),
  get: (id) => api.get(`/wizards/${id}`),
  update: (id, data) => api.put(`/wizards/${id}`, data),
  delete: (id) => api.delete(`/wizards/${id}`),
  clone: (id) => api.post(`/wizards/${id}/clone`),
  aiGenerate: (description, brand_id) => api.post('/wizards/ai-generate', { description, brand_id }),
  submissions: (id) => api.get(`/wizards/${id}/submissions`),
  // public
  public: (slug) => api.get(`/wizards/public/${slug}`),
  startDraft: (slug, email) => api.post(`/wizards/public/${slug}/draft`, { email }),
  getDraft: (slug, sid) => api.get(`/wizards/public/${slug}/draft/${sid}`),
  saveDraft: (slug, sid, data) => api.put(`/wizards/public/${slug}/draft/${sid}`, data),
  submit: (slug, sid, data) => api.post(`/wizards/public/${slug}/draft/${sid}/submit`, data),
};

export const leads = {
  list: (status) => api.get('/leads', { params: status ? { status } : {} }),
  get: (id) => api.get(`/leads/${id}`),
  approve: (id, data) => api.post(`/leads/${id}/approve`, data),
  reject: (id, reason) => api.post(`/leads/${id}/reject`, { reason }),
  blockClient: (cid) => api.post(`/leads/client/${cid}/block`),
  unblockClient: (cid) => api.post(`/leads/client/${cid}/unblock`),
  reprocessVault: (id) => api.post(`/leads/${id}/reprocess-vault`),
};

export const vaultTokens = {
  issue: (slug, sid, label, type='password', notes=null) =>
    api.post('/vault-tokens/issue', { slug, session_id: sid, label, type, notes }),
  publicGet: (token) => api.get(`/vault-tokens/public/${token}`),
  publicSubmit: (token, value) => api.post(`/vault-tokens/public/${token}/submit`, { value }),
  inline: (slug, sid, label, value, type='password', notes=null) =>
    api.post('/vault-tokens/inline', { slug, session_id: sid, label, value, type, notes }),
};

export const settings = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  projectTypes: () => api.get('/settings/project-types'),
  createProjectType: (data) => api.post('/settings/project-types', data),
  updateProjectType: (id, data) => api.put(`/settings/project-types/${id}`, data),
  deleteProjectType: (id) => api.delete(`/settings/project-types/${id}`),
  dashboardStats: () => api.get('/settings/dashboard-stats'),
  branding: () => api.get('/settings/public/branding'),
  testSmtp: (to, subject) => api.post('/settings/test-smtp', { to, subject }),
  testImap: () => api.post('/settings/test-imap', {}),
};


// ====== New feature APIs (Feb 2026) ======
export const today = {
  get: () => api.get('/today'),
};

export const statusReports = {
  list: (project_id) => api.get('/status-reports', { params: project_id ? { project_id } : {} }),
  get: (id) => api.get(`/status-reports/${id}`),
  generate: (project_id, days = 7) => api.post('/status-reports/generate', { project_id, days }),
  delete: (id) => api.delete(`/status-reports/${id}`),
};

export const nps = {
  list: (project_id) => api.get('/nps', { params: project_id ? { project_id } : {} }),
  invite: (project_id, email) => api.post('/nps/invite', { project_id, email }),
  publicGet: (token) => api.get(`/nps/public/${token}`),
  publicAnswer: (token, payload) => api.post(`/nps/public/${token}`, payload),
  request: (project_id) => api.post(`/nps/projects/${project_id}/request`),
  status: (project_id) => api.get(`/nps/projects/${project_id}/status`),
};

export const referrals = {
  list: (status) => api.get('/referrals', { params: status ? { status } : {} }),
  create: (data) => api.post('/referrals', data),
  convert: (id, project_id, project_value) => api.post(`/referrals/${id}/convert`, { project_id, project_value }),
  markPaid: (id) => api.post(`/referrals/${id}/mark-paid`),
  delete: (id) => api.delete(`/referrals/${id}`),
  aiDraftEmail: (id) => api.post(`/referrals/${id}/ai-draft-email`),
};

export const kb = {
  articles: (q) => api.get('/kb/articles', { params: q ? { q } : {} }),
  createArticle: (data) => api.post('/kb/articles', data),
  updateArticle: (id, data) => api.put(`/kb/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/kb/articles/${id}`),
  templates: (kind) => api.get('/kb/templates', { params: kind ? { kind } : {} }),
  createTemplate: (data) => api.post('/kb/templates', data),
  updateTemplate: (id, data) => api.put(`/kb/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/kb/templates/${id}`),
  aiGenerate: (description, kind, template_kind) => api.post('/kb/ai-generate', { description, kind, template_kind }),
};

export const inbox = {
  threads: () => api.get('/inbox/threads'),
  messages: (threadKey) => api.get(`/inbox/threads/${threadKey}/messages`),
  reply: (threadKey, content, also_send_email) => api.post(`/inbox/threads/${threadKey}/reply`, { content, also_send_email }),
  assign: (message_ids, project_id) => api.post('/inbox/assign', { message_ids, project_id }),
  projectsForAssignment: () => api.get('/inbox/clients-for-assignment'),
};

export const deliveryCalendar = {
  get: (year, month, include) => api.get('/delivery-calendar', { params: { year, month, include } }),
};

export const bankAccounts = {
  list: (brand_id) => api.get('/bank-accounts', { params: brand_id ? { brand_id } : {} }),
  create: (data) => api.post('/bank-accounts', data),
  update: (id, data) => api.put(`/bank-accounts/${id}`, data),
  delete: (id) => api.delete(`/bank-accounts/${id}`),
};

export const minisites = {
  list: () => api.get('/minisites'),
  upsert: (data) => api.post('/minisites', data),
  publicGet: (brandSlug) => api.get(`/minisites/public/${brandSlug}`),
};

export const changeRequests = {
  list: (project_id) => api.get('/change-requests', { params: project_id ? { project_id } : {} }),
  create: (data) => api.post('/change-requests', data),
  decide: (id, decision, note, extra) => api.post(`/change-requests/${id}/decide`, { decision, note, ...(extra || {}) }),
  delete: (id) => api.delete(`/change-requests/${id}`),
};

export const audit = {
  list: (params) => api.get('/audit', { params }),
  write: (data) => api.post('/audit', data),
};

export const search = {
  query: (q) => api.get('/search', { params: { q } }),
};

export const followups = {
  list: (project_id) => api.get('/followups', { params: project_id ? { project_id } : {} }),
  draft: (project_id, reason) => api.post('/followups/draft', { project_id, reason }),
  draftForItem: (kind, id) => api.post('/followups/draft-for-item', { kind, id }),
  send: (payload) => api.post('/followups/send', payload),
  delete: (id) => api.delete(`/followups/${id}`),
};

export const invoicesApi = {
  sendReminder: (project_id, inv_id) => api.post(`/projects/${project_id}/invoices/${inv_id}/send-reminder`),
};

export const leadScoring = {
  score: (lead_id) => api.post(`/lead-score/${lead_id}`),
};

export const meetings = {
  list: (project_id) => api.get('/meetings', { params: project_id ? { project_id } : {} }),
  get: (id) => api.get(`/meetings/${id}`),
  upload: (data) => api.post('/meetings', data),
  delete: (id) => api.delete(`/meetings/${id}`),
};

export const drive = {
  getConfig: () => api.get('/drive/config'),
  setConfig: (data) => api.put('/drive/config', data),
};


export const uploads = {
  getConfig: () => api.get('/uploads/config'),
  setConfig: (data) => api.put('/uploads/config', data),
  test: () => api.post('/uploads/test'),
  presign: (data) => api.post('/uploads/presign', data),
  confirm: (data) => api.post('/uploads/confirm', data),
  list: (params) => api.get('/uploads', { params }),
  get: (id) => api.get(`/uploads/${id}`),
  delete: (id) => api.delete(`/uploads/${id}`),
  wizardUpload: (slug, sessionId, file, fieldId) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('slug', slug);
    fd.append('session_id', sessionId);
    if (fieldId) fd.append('field_id', fieldId);
    return api.post('/uploads/wizard', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  setThumbnail: (id, thumbnail_key, thumbnail_url, duration_s) =>
    api.post(`/uploads/${id}/thumbnail`, { thumbnail_key, thumbnail_url, duration_s }),
  setWizardThumbnail: (slug, session_id, upload_id, thumbnail_key, thumbnail_url, duration_s) =>
    api.post('/uploads/wizard/thumbnail', { slug, session_id, upload_id, thumbnail_key, thumbnail_url, duration_s }),
  setTrim: (id, start_s, end_s) => api.patch(`/uploads/${id}/trim`, { start_s, end_s }),
  addAnnotation: (id, timestamp_s, note) => api.post(`/uploads/${id}/annotations`, { timestamp_s, note }),
  deleteAnnotation: (id, annId) => api.delete(`/uploads/${id}/annotations/${annId}`),
};

export const zoom = {
  getConfig: () => api.get('/zoom/config'),
  setConfig: (data) => api.put('/zoom/config', data),
  test: () => api.post('/zoom/test'),
  createMeeting: (data) => api.post('/zoom/meetings', data),
  listMeetings: (params) => api.get('/zoom/meetings', { params }),
  cancelMeeting: (id) => api.delete(`/zoom/meetings/${id}`),
};


export const triggers = {
  catalog: () => api.get('/triggers/catalog'),
  test: (channel_id, event) => api.post('/triggers/test', { channel_id, event }),
};
